import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Flex, Typography, message, Result } from 'antd';
import { 
  RocketOutlined, 
  CloudDownloadOutlined, 
  MobileOutlined, 
  LoadingOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;

// --- 配置项 ---
const CONFIG = {
  logo: "/logo1024.png", // 替换为你的APP Logo
  appName: "狐哩狐哩",
  defaultScheme: "ddlynne://(tabs)/index", // 默认唤起路径
  downloadPage: "/app/download", // 你的下载页面路由地址
  security: {
    allowedSchemes: ["ddlynne://"], // 只允许这个协议
    allowedPathPattern: /^ddlynne:\/\/(voice|theme|pack)\/?.*$/ 
  }
};

const RedirectPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'trying' | 'failed'>('trying');

  const safeTargetUrl = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const rawUrl = searchParams.get('url');

    // 1. 如果没有参数，返回默认安全路径
    if (!rawUrl) return CONFIG.defaultScheme;

    try {
      const decodedUrl = decodeURIComponent(rawUrl).trim();

      // 2. 检查协议头 (Protocol Check)
      // 防止 http/https 跳转，防止 javascript: 脚本执行
      const isSchemeValid = CONFIG.security.allowedSchemes.some(scheme => 
        decodedUrl.startsWith(scheme)
      );

      if (!isSchemeValid) {
        console.warn("拦截非法协议跳转:", decodedUrl);
        return CONFIG.defaultScheme;
      }

      // 3. (可选) 正则格式校验 (Format Check)
      // 如果你的APP Scheme结构比较固定，建议开启此项，防止畸形数据
      if (CONFIG.security.allowedPathPattern) {
        if (!CONFIG.security.allowedPathPattern.test(decodedUrl)) {
             console.warn("拦截非法路径格式:", decodedUrl);
             return CONFIG.defaultScheme;
        }
      }

      // 4. 防止控制字符 (Anti-XSS 补充)
      // 移除可能存在的不可见控制字符
      /* eslint-disable-next-line no-control-regex */
      if (/[\x00-\x1F\x7F]/.test(decodedUrl)) {
         return CONFIG.defaultScheme;
      }

      return decodedUrl;
    } catch (e) {
      // 解析出错，直接回退
      return CONFIG.defaultScheme;
    }
  }, [location.search]);

  // 执行唤起逻辑
  const openApp = (isAuto = false) => {
    if (isAuto) setStatus('trying');
    
    // 1. 尝试跳转 Scheme
    window.location.href = safeTargetUrl;

    // 2. 设置超时检测 (H5 通用检测方案)
    const startTime = Date.now();
    const delay = 2500;

    setTimeout(() => {
      const endTime = Date.now();
      // 如果页面没有隐藏（说明没跳走），且时间差在阈值内，说明唤起失败
      if (!document.hidden && (endTime - startTime < delay + 200)) {
        setStatus('failed');
        if (!isAuto) {
            message.warning('唤起失败，请确认是否已安装 APP');
        }
      }
    }, delay);
  };

  // 页面挂载时自动执行一次
  useEffect(() => {
    // 延迟 500ms 执行，让用户看到一点动画，体验更好，也能避免某些浏览器过快拦截
    const timer = setTimeout(() => {
        openApp(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [safeTargetUrl]);

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      position: 'relative',
      overflow: 'hidden',
      padding: 20
    }}>
      {/* --- CSS 动画定义 --- */}
      <style>{`
        @keyframes gradientBG {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes breathe {
          0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7); }
          50% { transform: scale(1.05); box-shadow: 0 0 20px 10px rgba(255, 255, 255, 0); }
          100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
        }
        @keyframes ripple {
            0% { transform: scale(0.8); opacity: 1; }
            100% { transform: scale(2); opacity: 0; }
        }
        .bg-animate {
            background: linear-gradient(-45deg, #ee7752, #e73c7e, #23a6d5, #23d5ab);
            background-size: 400% 400%;
            animation: gradientBG 15s ease infinite;
            position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 0;
        }
        .breathing-logo {
            animation: breathe 3s ease-in-out infinite;
        }
        .ripple-circle {
            position: absolute;
            width: 100px; height: 100px;
            border-radius: 50%;
            border: 2px solid rgba(255,255,255,0.5);
            top: 50%; left: 50%;
            margin-top: -50px; margin-left: -50px;
            animation: ripple 2s linear infinite;
            z-index: -1;
        }
        .glass-panel {
            background: rgba(255, 255, 255, 0.85);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.5);
            border-radius: 24px;
            box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.2);
            z-index: 1;
        }
      `}</style>

      {/* 动态背景 */}
      <div className="bg-animate" />

      {/* 主卡片 */}
      <Card 
        bordered={false} 
        className="glass-panel"
        style={{ width: '100%', maxWidth: 400, textAlign: 'center' }}
      >
        <Flex vertical align="center" gap={20} style={{ padding: '20px 0' }}>
            
            {/* 呼吸 Logo 区域 */}
            <div style={{ position: 'relative', width: 100, height: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {status === 'trying' && <div className="ripple-circle" />}
                <img 
                    src={CONFIG.logo} 
                    alt="App Logo" 
                    className={status === 'trying' ? "breathing-logo" : ""}
                    style={{ 
                        width: 80, height: 80, 
                        borderRadius: 20, 
                        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                        zIndex: 2,
                        transition: 'filter 0.3s'
                    }} 
                />
            </div>

            {/* 文本提示 */}
            <div>
                <Title level={3} style={{ marginBottom: 5 }}>
                    {status === 'trying' ? '正在唤起...' : '唤起失败'}
                </Title>
                <Paragraph type="secondary" style={{ fontSize: 14 }}>
                    {status === 'trying' 
                        ? `正在前往 ${CONFIG.appName}，请在弹窗中选择允许` 
                        : '似乎没有检测到 APP，或者操作被拦截'
                    }
                </Paragraph>
            </div>

            {/* 操作按钮组 */}
            <Flex vertical gap={12} style={{ width: '100%' }}>
                {/* 失败时显示主操作：下载 */}
                {status === 'failed' && (
                    <Button 
                        type="primary" 
                        shape="round" 
                        size="large" 
                        icon={<CloudDownloadOutlined />}
                        block
                        style={{ height: 48, fontSize: 16, background: 'linear-gradient(90deg, #1677ff, #38ef7d)', border: 0 }}
                        onClick={() => navigate(CONFIG.downloadPage)}
                    >
                        去下载客户端
                    </Button>
                )}

                {/* 始终显示：手动打开 */}
                <Button 
                    type={status === 'trying' ? 'primary' : 'default'} // 尝试中时此为主按钮，失败时降级
                    shape="round" 
                    size="large" 
                    icon={status === 'trying' ? <RocketOutlined /> : <MobileOutlined />}
                    block
                    style={{ height: 48, fontSize: 16 }}
                    onClick={() => openApp(false)}
                >
                    {status === 'trying' ? '手动打开 APP' : '再次尝试打开'}
                </Button>
                
                {status === 'failed' && (
                    <Text type="secondary" style={{ fontSize: 12, marginTop: 10 }}>
                        如果您已安装，请点击"再次尝试打开"
                    </Text>
                )}
            </Flex>
        </Flex>
      </Card>
      
      {/* 底部版权 */}
      <div style={{ position: 'absolute', bottom: 20, color: 'rgba(255,255,255,0.6)', fontSize: 12, zIndex: 1 }}>
         © {CONFIG.appName} <a href="https://beian.miit.gov.cn/">京ICP备2024099964号-2</a>
      </div>
    </div>
  );
};

export default RedirectPage;